import * as React from 'react';
import { DefaultButton, MessageBar, MessageBarType, Pivot, PivotItem, PrimaryButton } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { ChangeType, ConfidenceBucket, IChangeDecision, IOrganizationPlan, IPlanPreview } from '../../../models/Plan';
import { confidenceBucket } from '../../../models/Plan';
import { knownNames } from '../../../tagging/knownNames';
import type { IScanResult } from '../../../models/ScanResult';
import { type IResolvedChange, resolveChange } from '../../../plan/PlanSimulator';
import ChangeCard from './ChangeCard';
import { BUCKET_LABEL } from './planDisplay';
import { formatNumber } from '../../../utils/format';
import type { IAiNamingService, INameRequest } from '../../../ai/AiNaming';
import { describeGraphError } from '../../../utils/errors';

export interface IPlanViewProps {
  scan: IScanResult;
  plan: IOrganizationPlan;
  decisions: { [changeId: string]: IChangeDecision };
  /** Preview including pending changes, used to warn about conflicts on each card. */
  preview: IPlanPreview;
  onDecide: (changeId: string, decision: IChangeDecision) => void;
  onApproveMany: (changeIds: string[]) => void;
  onReset: () => void;
  /** Recommend-only mode when false: the Apply button is disabled. */
  allowChanges: boolean;
  onApply: () => void;
  /** AI naming, when available (web app); the button is hidden otherwise. */
  ai?: IAiNamingService;
  /** Applies several edits at once (AI suggestions). */
  onDecideMany?: (decisions: { [changeId: string]: IChangeDecision }) => void;
}

type Filter = 'all' | ConfidenceBucket | 'approved' | 'rejected';

const FILTERS: Filter[] = ['all', 'high', 'medium', 'low', 'needsInput', 'approved', 'rejected'];

const FILTER_LABEL: { [f in Filter]: string } = {
  all: 'All',
  high: BUCKET_LABEL.high,
  medium: BUCKET_LABEL.medium,
  low: BUCKET_LABEL.low,
  needsInput: BUCKET_LABEL.needsInput,
  approved: 'Approved',
  rejected: 'Rejected'
};

function matches(r: IResolvedChange, filter: Filter): boolean {
  if (filter === 'all') { return true; }
  if (filter === 'approved' || filter === 'rejected') { return r.status === filter; }
  return r.status === 'pending' && confidenceBucket(r.change) === filter;
}

function plural(n: number, one: string, many: string): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

/** Below this, AI suggestions are marked for a careful check. */
const LOW_CONFIDENCE: number = 0.5;

/** The organization plan: every proposed change, grouped by confidence, for human review. */
export default function PlanView(props: IPlanViewProps): React.ReactElement {
  const { scan, plan, decisions, preview, onDecide, onApproveMany, onReset, allowChanges, onApply, ai, onDecideMany } = props;
  const [aiBusy, setAiBusy] = React.useState<boolean>(false);
  const [aiMessage, setAiMessage] = React.useState<{ ok: boolean; text: string } | undefined>(undefined);
  const [filter, setFilter] = React.useState<Filter>('all');

  const resolved: IResolvedChange[] = plan.changes.map(c => resolveChange(scan, c, decisions[c.id]));
  const byType = (type: ChangeType): number => plan.changes.filter(c => c.type === type).length;
  const needsName: number = plan.changes.filter(c => c.type === 'renameFile' && c.needsInput).length;

  const conflictsFor: { [changeId: string]: string[] } = {};
  for (const conflict of preview.conflicts) {
    for (const id of conflict.changeIds) {
      (conflictsFor[id] = conflictsFor[id] || []).push(conflict.message);
    }
  }

  const approved: number = resolved.filter(r => r.status === 'approved').length;
  const rejected: number = resolved.filter(r => r.status === 'rejected').length;
  const highReady: string[] = resolved
    .filter(r => r.status === 'pending' && !r.error && confidenceBucket(r.change) === 'high' && !conflictsFor[r.change.id])
    .map(r => r.change.id);
  const visible: IResolvedChange[] = resolved.filter(r => matches(r, filter));
  const applicable: number = resolved.filter(r => r.status === 'approved' && !r.error).length;
  // Renames still waiting for a name: what the AI can help with. Sent in groups of 20.
  const unnamed: IResolvedChange[] = resolved.filter(r => r.change.type === 'renameFile' && r.status === 'pending' && !r.newName);

  const suggestNames = (): void => {
    if (!ai || !onDecideMany) { return; }
    const requests: INameRequest[] = unnamed.map(r => {
      const file = scan.files[r.change.targetId];
      return { changeId: r.change.id, fileId: file.id, name: file.name, extension: file.extension, folderPath: file.parentPath };
    });
    setAiBusy(true);
    setAiMessage(undefined);
    const batches: INameRequest[][] = [];
    for (let i = 0; i < requests.length; i += 20) {
      batches.push(requests.slice(i, i + 20));
    }
    let named: number = 0;
    let used: number = 0;
    const known = knownNames(scan);
    const run = (index: number): Promise<void> => {
      if (index >= batches.length) { return Promise.resolve(); }
      return ai.suggestNames(scan.library.id, batches[index], known).then(result => {
        const next: { [changeId: string]: IChangeDecision } = {};
        for (const s of result.suggestions) {
          next[s.changeId] = {
            ...(decisions[s.changeId] ?? { status: 'pending' }),
            newName: s.name,
            note: `AI suggestion (${Math.round(s.confidence * 100)}%${s.confidence < LOW_CONFIDENCE ? ', low confidence — check carefully' : ''}): ${s.reason}`
          };
        }
        named += result.suggestions.length;
        used += result.creditsUsed;
        onDecideMany(next);
        return run(index + 1);
      });
    };
    run(0)
      .then(() => setAiMessage(named > 0
        ? { ok: true, text: `AI suggested ${plural(named, 'name', 'names')} using ${plural(used, 'credit', 'credits')}. Review each one, then approve.` }
        : { ok: false, text: 'AI could not suggest any names — it could not read text from these files. No credits were used.' }))
      .catch(error => setAiMessage({ ok: false, text: named > 0
        ? `AI suggested ${plural(named, 'name', 'names')}, then stopped: ${describeGraphError(error)}`
        : describeGraphError(error) }))
      .then(() => setAiBusy(false), () => setAiBusy(false));
  };

  if (plan.changes.length === 0) {
    return <MessageBar>No changes to suggest — the rules found nothing they can fix automatically.</MessageBar>;
  }

  return (
    <div className={styles.plan}>
      <h3 className={styles.sectionTitle}>
        Organization plan <span className={styles.sectionCount}>{plural(plan.changes.length, 'change', 'changes')}</span>
      </h3>
      <ul className={styles.planSummary}>
        {byType('quarantineFile') > 0 && <li>{plural(byType('quarantineFile'), 'file', 'files')} to quarantine</li>}
        {byType('quarantineFolder') > 0 && <li>{plural(byType('quarantineFolder'), 'empty folder', 'empty folders')} to quarantine</li>}
        {byType('mergeFolders') > 0 && <li>{plural(byType('mergeFolders'), 'folder consolidation', 'folder consolidations')}</li>}
        {byType('moveFolder') > 0 && <li>{plural(byType('moveFolder'), 'folder move', 'folder moves')}</li>}
        {byType('flattenFolders') > 0 && <li>{plural(byType('flattenFolders'), 'over-nested branch', 'over-nested branches')} to flatten</li>}
        {byType('renameFile') > 0 && <li>{plural(byType('renameFile'), 'rename', 'renames')}{needsName > 0 && ` (${formatNumber(needsName)} need a name from you)`}</li>}
      </ul>

      {allowChanges ? (
        <MessageBar isMultiline>
          Approve the changes you want, then choose <strong>Apply approved changes</strong>. Every change is checked
          against SharePoint and confirmed before anything is written. Quarantine moves items to{' '}
          <strong>{plan.quarantinePath}</strong> in this library — nothing is ever deleted. Decisions are kept only
          until you rescan or reload the page.
        </MessageBar>
      ) : (
        <MessageBar isMultiline>
          <strong>Recommend-only mode.</strong> You can review and approve changes, but nothing can be applied to
          SharePoint. To allow changes, edit this web part and turn on <strong>Allow changes to this site</strong>.
        </MessageBar>
      )}

      <div className={styles.planToolbar}>
        <span className={styles.planProgress}>
          {formatNumber(approved)} approved · {formatNumber(rejected)} rejected · {formatNumber(plan.changes.length - approved - rejected)} to review
        </span>
        <PrimaryButton
          text={`Approve all high confidence (${formatNumber(highReady.length)})`}
          iconProps={{ iconName: 'CheckList' }}
          disabled={highReady.length === 0}
          onClick={() => onApproveMany(highReady)}
        />
        <DefaultButton text="Reset decisions" iconProps={{ iconName: 'Refresh' }} disabled={approved + rejected === 0} onClick={onReset} />
        {ai && onDecideMany && (
          <DefaultButton
            text={aiBusy ? 'Asking AI…' : `Suggest names with AI (${plural(unnamed.length, 'file', 'files')} · ${plural(unnamed.length * ai.creditsPerFile, 'credit', 'credits')})`}
            iconProps={{ iconName: 'Lightbulb' }}
            disabled={aiBusy || unnamed.length === 0}
            onClick={suggestNames}
          />
        )}
        <PrimaryButton
          text={`Apply approved changes (${formatNumber(applicable)})`}
          iconProps={{ iconName: 'Play' }}
          disabled={!allowChanges || applicable === 0}
          title={allowChanges ? undefined : 'Recommend-only mode: turn on "Allow changes to this site" in the web part settings.'}
          onClick={onApply}
        />
      </div>

      {aiMessage && (
        <MessageBar messageBarType={aiMessage.ok ? MessageBarType.success : MessageBarType.warning} isMultiline onDismiss={() => setAiMessage(undefined)}>
          {aiMessage.text}
        </MessageBar>
      )}

      <Pivot
        headersOnly
        selectedKey={filter}
        onLinkClick={item => item && setFilter(item.props.itemKey as Filter)}
        className={styles.planFilters}
      >
        {FILTERS.map(f => (
          <PivotItem key={f} itemKey={f} headerText={FILTER_LABEL[f]} itemCount={resolved.filter(r => matches(r, f)).length} />
        ))}
      </Pivot>

      {visible.length === 0
        ? <p className={styles.issueListDescription}>Nothing here.</p>
        : (
          <ul className={styles.changes}>
            {visible.map(r => (
              <ChangeCard
                key={r.change.id}
                scan={scan}
                plan={plan}
                resolved={r}
                decision={decisions[r.change.id]}
                conflicts={conflictsFor[r.change.id] || []}
                onDecide={d => onDecide(r.change.id, d)}
              />
            ))}
          </ul>
        )}
    </div>
  );
}
